import { MODELED_GL_OPERATING_COST_SOURCE_SYSTEM_REF } from './approved-source-systems';
import type {
  ApprovedFormulaDefinition,
  ApprovedFormulaImplementationDigestInput,
  ApprovedFormulaResult,
} from './approved-formulas';

export const GL_SCOPED_REDUCTION_FORMULA_ID = 'gl_scoped_reduction_v1';
export const GL_SCOPED_REDUCTION_FORMULA_VERSION = '1.0.0';
export const GL_SCOPED_REDUCTION_CONTEXT_KIND =
  'decision_agent:gl_data_lake:monthly_aggregate';

const BASIS_POINTS_PER_PERCENTAGE = 10_000;
const ANNUAL_MONTHS = 12;

export type GlCostBasis = 'expense_accrual';
export type GlScenarioTiming = 'historical_counterfactual' | 'forward_savings';
export type GlBaselineSourceClass = 'modeled' | 'connector-derived';
export type GlAnnualizationAssumption =
  | 'none'
  | 'annualize_complete_coverage';

/**
 * The scenario engine intentionally receives server-computed cost totals and
 * immutable cell/provenance digests, rather than arbitrary client table rows.
 * This flat shape is also the exact modeled-runtime operand mapping.
 */
export type GlScopedReductionInput = Readonly<{
  sourceRunId: string;
  baselineDatasetId: string;
  baselineRunId: string;
  baselineSourceClass: GlBaselineSourceClass;
  baselineSnapshotDigest: string;
  queryDigest: string;
  eligibleCellSetDigest: string;
  excludedCellSetDigest: string;
  costBasis: GlCostBasis;
  baselineEligibleExpenseUsd: number;
  excludedExpenseUsd: number;
  reductionRateBps: number;
  scenarioTiming: GlScenarioTiming;
  coveredMonths: number;
  expectedMonths: number;
  annualizationAssumption: GlAnnualizationAssumption;
}>;

export type GlScopedReductionOutput = Readonly<{
  baselineEligibleExpenseUsd: number;
  excludedExpenseUsd: number;
  selectedExpenseUsd: number;
  reductionRateBps: number;
  reductionAmountUsd: number;
  remainingEligibleExpenseUsd: number;
  historicalCounterfactualSavingsUsd: number | null;
  forwardInPeriodSavingsUsd: number | null;
  annualRunRateSavingsUsd: number | null;
}>;

type FormulaBlocked = Extract<
  ApprovedFormulaResult<never>,
  Readonly<{ status: 'blocked' }>
>;

export function executeGlScopedReduction(
  input: GlScopedReductionInput,
): ApprovedFormulaResult<GlScopedReductionOutput> {
  const validation = validateGlScopedReductionInput(input);
  if (validation) return validation;
  const reductionAmountUsd = money(
    (input.baselineEligibleExpenseUsd * input.reductionRateBps) /
      BASIS_POINTS_PER_PERCENTAGE,
  );
  const coverageComplete = input.coveredMonths === input.expectedMonths;
  const forward = input.scenarioTiming === 'forward_savings';
  return {
    status: 'ok',
    output: {
      baselineEligibleExpenseUsd: money(input.baselineEligibleExpenseUsd),
      excludedExpenseUsd: money(input.excludedExpenseUsd),
      selectedExpenseUsd: money(
        input.baselineEligibleExpenseUsd + input.excludedExpenseUsd,
      ),
      reductionRateBps: input.reductionRateBps,
      reductionAmountUsd,
      remainingEligibleExpenseUsd: money(
        input.baselineEligibleExpenseUsd - reductionAmountUsd,
      ),
      historicalCounterfactualSavingsUsd: forward ? null : reductionAmountUsd,
      forwardInPeriodSavingsUsd:
        forward && coverageComplete ? reductionAmountUsd : null,
      annualRunRateSavingsUsd:
        forward && input.annualizationAssumption === 'annualize_complete_coverage'
          ? money((reductionAmountUsd * ANNUAL_MONTHS) / input.coveredMonths)
          : null,
    },
  };
}

export const GL_SCOPED_REDUCTION_APPROVED_FORMULA_DEFINITION: ApprovedFormulaDefinition<
  GlScopedReductionInput,
  GlScopedReductionOutput
> = Object.freeze({
  formulaId: GL_SCOPED_REDUCTION_FORMULA_ID,
  version: GL_SCOPED_REDUCTION_FORMULA_VERSION,
  displayName: 'GL scoped reduction scenario',
  description:
    'Applies one declared percentage reduction to a server-selected, de-duplicated expense-accrual scope while keeping exclusions and timing posture explicit.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'gl_scoped_reduction_input_v1',
  outputSchemaVersion: 'gl_scoped_reduction_output_v1',
  expectedContextKind: GL_SCOPED_REDUCTION_CONTEXT_KIND,
  approvedSourceSystemRefs: Object.freeze([
    MODELED_GL_OPERATING_COST_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: Object.freeze([
    digestInput('executeGlScopedReduction', executeGlScopedReduction),
    digestInput('validateGlScopedReductionInput', validateGlScopedReductionInput),
    digestInput('money', money),
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'gl_scoped_reduction_v1:historical-counterfactual:v1',
      input: Object.freeze({
        sourceRunId: 'what-if-gl-reduction-golden',
        baselineDatasetId: 'chrw-functional-gl',
        baselineRunId: 'gl-run-golden',
        baselineSourceClass: 'modeled' as const,
        baselineSnapshotDigest: 'a'.repeat(64),
        queryDigest: 'b'.repeat(64),
        eligibleCellSetDigest: 'c'.repeat(64),
        excludedCellSetDigest: 'd'.repeat(64),
        costBasis: 'expense_accrual' as const,
        baselineEligibleExpenseUsd: 900,
        excludedExpenseUsd: 100,
        reductionRateBps: 1000,
        scenarioTiming: 'historical_counterfactual' as const,
        coveredMonths: 3,
        expectedMonths: 3,
        annualizationAssumption: 'none' as const,
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          baselineEligibleExpenseUsd: 900,
          excludedExpenseUsd: 100,
          selectedExpenseUsd: 1000,
          reductionRateBps: 1000,
          reductionAmountUsd: 90,
          remainingEligibleExpenseUsd: 810,
          historicalCounterfactualSavingsUsd: 90,
          forwardInPeriodSavingsUsd: null,
          annualRunRateSavingsUsd: null,
        }),
      }),
    }),
  ]),
  execute: executeGlScopedReduction,
});

function validateGlScopedReductionInput(
  input: GlScopedReductionInput,
): FormulaBlocked | null {
  if (!input || typeof input !== 'object') {
    return blocked('missing_formula_input', 'GL reduction requires a scenario input.');
  }
  const textValues = [
    input.sourceRunId, input.baselineDatasetId, input.baselineRunId,
    input.baselineSnapshotDigest, input.queryDigest, input.eligibleCellSetDigest,
    input.excludedCellSetDigest,
  ];
  if (!textValues.every(nonBlank) || !digestValuesValid(input)) {
    return blocked('missing_formula_input', 'GL reduction requires immutable snapshot and query provenance.');
  }
  if (input.costBasis !== 'expense_accrual') {
    return blocked('invalid_formula_input', 'GL reduction supports expense-accrual cost basis only.');
  }
  if (input.baselineSourceClass !== 'modeled' && input.baselineSourceClass !== 'connector-derived') {
    return blocked('invalid_formula_input', 'GL reduction requires modeled or connector-derived baseline provenance.');
  }
  if (!nonNegativeMoney(input.baselineEligibleExpenseUsd) || !finiteMoney(input.excludedExpenseUsd)) {
    return blocked('invalid_formula_input', 'GL reduction requires a non-negative eligible cost and finite exclusions.');
  }
  if (!Number.isInteger(input.reductionRateBps) || input.reductionRateBps < 0 || input.reductionRateBps > BASIS_POINTS_PER_PERCENTAGE) {
    return blocked('invalid_formula_input', 'GL reduction rate must be an integer from 0 through 10,000 basis points.');
  }
  if (!validCoverage(input.coveredMonths, input.expectedMonths)) {
    return blocked('missing_formula_input', 'GL reduction requires complete declared period coverage.');
  }
  if (input.scenarioTiming !== 'historical_counterfactual' && input.scenarioTiming !== 'forward_savings') {
    return blocked('invalid_formula_input', 'GL reduction timing must be historical counterfactual or forward savings.');
  }
  if (!validAnnualization(input)) {
    return blocked('invalid_formula_input', 'Annualized savings require a complete-coverage forward-savings assumption.');
  }
  return null;
}

function digestValuesValid(input: GlScopedReductionInput): boolean {
  return [input.baselineSnapshotDigest, input.queryDigest, input.eligibleCellSetDigest, input.excludedCellSetDigest]
    .every((digest) => /^[a-f0-9]{64}$/i.test(digest));
}

function validCoverage(coveredMonths: number, expectedMonths: number): boolean {
  return Number.isInteger(coveredMonths) && Number.isInteger(expectedMonths) && coveredMonths > 0 && expectedMonths > 0 && coveredMonths <= expectedMonths;
}

function validAnnualization(input: GlScopedReductionInput): boolean {
  if (input.annualizationAssumption === 'none') return true;
  return input.annualizationAssumption === 'annualize_complete_coverage' &&
    input.scenarioTiming === 'forward_savings' && input.coveredMonths === input.expectedMonths;
}

function nonBlank(value: string): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function finiteMoney(value: number): boolean {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  const cents = Math.round(value * 100);
  return Number.isSafeInteger(cents) && cents / 100 === value;
}

function nonNegativeMoney(value: number): boolean {
  return finiteMoney(value) && value >= 0;
}

function money(value: number): number {
  return Math.round(value * 100) / 100;
}

function blocked(
  reasonCode: FormulaBlocked['reasonCode'],
  message: string,
): FormulaBlocked {
  return { status: 'blocked', reasonCode, message };
}

function digestInput(
  name: string,
  implementation: unknown,
): ApprovedFormulaImplementationDigestInput {
  return Object.freeze({ name, source: String(implementation) });
}
